'use client';

import {
  FilterBar,
  type TFilterBarPriceRange,
} from '@chemist4u/newt-components';
import { DEFAULT_PLP_SORT, parsePageParam } from '@lib/util/pagination';
import { type SortOptions, sortOptions } from '@lib/util/sort-products';
import { PRODUCTS_PAGE_SIZE } from '@modules/store/lib/product-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

type ProductToolbarProps = {
  sortBy: SortOptions;
  productCount: number;
  minPrice?: number;
  maxPrice?: number;
  /** True while a client-side sort/filter override is fetching its first
   * page — `productCount` is 0 at that point, so the results label would
   * otherwise misleadingly read "0 of 0 products". */
  isLoading?: boolean;
  /** Navigates to a new PLP URL — pass the caller's `useNavigationLoading().navigate`
   * so its `isPending` state can drive a spinner elsewhere in the same
   * section. Defaults to a plain `router.push` (no pending signal) when
   * omitted. */
  navigate?: (href: string) => void;
};

const ProductToolbar = ({
  sortBy,
  productCount,
  minPrice,
  maxPrice,
  isLoading = false,
  navigate: navigateProp,
}: ProductToolbarProps) => {
  const router = useRouter();
  const navigate = navigateProp ?? ((href: string) => router.push(href));
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const page = parsePageParam(searchParams.get('page'));

  const setQueryParams = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams);
      for (const [name, value] of Object.entries(updates)) {
        if (value === null) {
          params.delete(name);
        } else {
          params.set(name, value);
        }
      }
      // Back to page 1 — expressed as no `page` param at all, matching
      // `buildPageHref`'s canonical page-1 form.
      params.delete('page');
      const query = params.toString();
      navigate(query ? `${pathname}?${query}` : pathname);
    },
    [searchParams, pathname, navigate],
  );

  const handleSortChange = (value: string) => {
    // The default sort is removed, not written — keeps the URL in its
    // clean, canonical no-param form (mirrors `buildPlpLinkParams`).
    setQueryParams({ sortBy: value === DEFAULT_PLP_SORT ? null : value });
  };

  const handlePriceApply = ({ min, max }: TFilterBarPriceRange) => {
    setQueryParams({
      minPrice: min != null ? String(min) : null,
      maxPrice: max != null ? String(max) : null,
    });
  };

  return (
    <div className="relative z-[25] mb-4">
      <FilterBar
        page={page}
        pageSize={PRODUCTS_PAGE_SIZE}
        totalItems={productCount}
        // No label override when loaded — FilterBar falls back to the DS
        // range format ("1 - 24 of 111 products"), matching the Pagination
        // results count below the grid.
        resultsLabel={isLoading ? 'Loading products…' : undefined}
        sortValue={sortBy}
        sortOptions={sortOptions}
        onSortChange={handleSortChange}
        priceMin={minPrice ?? null}
        priceMax={maxPrice ?? null}
        onPriceApply={handlePriceApply}
      />
    </div>
  );
};

export default ProductToolbar;
